import React, { createContext, useContext, useEffect, useState } from 'react';

export interface Theme {
  colors: {
    primary: string;
    secondary: string;
    background: string;
    foreground: string;
  };
  fontFamily: string;
  spacing: string;
  logo: string;
}

const defaultTheme: Theme = {
  colors: {
    primary: '222.2 47.4% 11.2%',
    secondary: '210 40% 96.1%',
    background: '0 0% 100%',
    foreground: '222.2 84% 4.9%',
  },
  fontFamily: 'Inter, sans-serif',
  spacing: '1rem',
  logo: '/logo.png',
};

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Partial<Theme>) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: defaultTheme,
  setTheme: () => {},
});

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [theme, setThemeState] = useState<Theme>(defaultTheme);

  const setTheme = (value: Partial<Theme>) => {
    setThemeState((prev) => ({ ...prev, ...value }));
  };

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--primary', theme.colors.primary);
    root.style.setProperty('--secondary', theme.colors.secondary);
    root.style.setProperty('--background', theme.colors.background);
    root.style.setProperty('--foreground', theme.colors.foreground);
    root.style.setProperty('--font-family', theme.fontFamily);
    root.style.setProperty('--spacing', theme.spacing);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
